import { Badge, Button, Text } from "@/components/ui";
import type { MatterPhaseAction } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatDateTime } from "@/lib/utils/format";
import dayjs from "dayjs";
import {
  ListTodo,
  CheckCircle2,
  Circle,
  Clock3,
  Lock,
  RotateCcw,
  ShieldAlert,
} from "lucide-react";
import { getActionProgress } from "./actionProgress";
import { MatterPhaseActionInfo } from "./MatterPhaseActionInfo";

interface MatterPhaseActionCardProps {
  grouped?: boolean;
  taskButtonLabel?: "Create task" | "View task";
  onTaskClick?: () => void;
  action: MatterPhaseAction;
  phaseIsClosed: boolean;
  canOverride: boolean;
  canRetry: boolean;
  retryPending: boolean;
  onRetry: () => void;
  onSetStatus: (status: "Blocked" | "Closed") => void;
  onClearStatus: () => void;
  completionPending: boolean;
  onToggleCompletion: (completed: boolean) => void;
}

const statusStyles = {
  Complete: {
    icon: CheckCircle2,
    badge: "border-emerald-200 bg-emerald-50 text-emerald-800",
    progress: "bg-emerald-500",
  },
  Blocked: {
    icon: ShieldAlert,
    badge: "border-red-200 bg-red-50 text-red-800",
    progress: "bg-red-500",
  },
  Closed: {
    icon: Lock,
    badge: "border-slate-200 bg-slate-100 text-slate-700",
    progress: "bg-slate-500",
  },
  Incomplete: {
    icon: Circle,
    badge: "border-border bg-muted/50 text-muted-foreground",
    progress: "bg-amber-400",
  },
  Active: {
    icon: Clock3,
    badge: "border-amber-200 bg-amber-50 text-amber-800",
    progress: "bg-amber-400",
  },
};

export function MatterPhaseActionCard({
  grouped = false,
  taskButtonLabel,
  onTaskClick,
  action,
  phaseIsClosed,
  canOverride,
  canRetry,
  retryPending,
  onRetry,
  onSetStatus,
  onClearStatus,
  completionPending,
  onToggleCompletion,
}: MatterPhaseActionCardProps) {
  const progress = getActionProgress(action);
  const partialProgress =
    action.effectiveStatus === "Incomplete" && (progress?.completed ?? 0) > 0;
  const style =
    statusStyles[partialProgress ? "Active" : action.effectiveStatus];
  const StatusIcon = style.icon;
  const checkedAt = action.lastEvaluatedAt
    ? formatDateTime(dayjs.utc(action.lastEvaluatedAt).toDate())
    : null;

  return (
    <div
      id={`phase-action-${action.id}`}
      className={cn(
        "flex min-w-0 flex-col gap-3 py-4",
        grouped
          ? "border-border/70 border-b last:border-b-0"
          : "border-border/70 bg-card min-h-44 rounded-2xl border p-5",
      )}
    >
      <div className="min-w-0 space-y-2">
        <div className="flex items-start gap-2">
          <Text
            variant="body"
            className="min-w-0 text-sm font-medium [overflow-wrap:anywhere]"
          >
            {action.title}
          </Text>
          <MatterPhaseActionInfo
            title={action.title}
            description={action.description}
            checkedAt={checkedAt}
            systemAction={action.kind !== "Manual"}
            canRetry={
              canRetry && action.effectiveStatus !== "Closed" && !phaseIsClosed
            }
            retryPending={retryPending}
            onRetry={onRetry}
          />
        </div>
        {progress ? (
          <div className="space-y-2">
            <Text variant="caption">{progress.label}</Text>
            {progress.total && progress.completed !== undefined ? (
              <div
                role="progressbar"
                aria-label={`${action.title} progress`}
                aria-valuemin={0}
                aria-valuemax={progress.total}
                aria-valuenow={progress.completed}
                aria-valuetext={progress.label}
                className="bg-muted h-1.5 overflow-hidden rounded-full"
              >
                <div
                  className={cn(
                    "h-full rounded-full transition-[width]",
                    style.progress,
                  )}
                  style={{
                    width: `${(progress.completed / progress.total) * 100}%`,
                  }}
                />
              </div>
            ) : null}
            {action.derivedStatus === "Incomplete" &&
            progress.total &&
            progress.completed === progress.total ? (
              <Text variant="caption">Additional requirements remain.</Text>
            ) : null}
          </div>
        ) : null}
        {action.manualOverrideReason ? (
          <Text
            variant="caption"
            className={cn(
              "[overflow-wrap:anywhere]",
              action.manualOverride === "Blocked"
                ? "text-red-800 dark:text-red-400"
                : "text-slate-700 dark:text-slate-300",
            )}
          >
            {action.manualOverride}: {action.manualOverrideReason}
            {action.manualOverrideSetAt
              ? ` · ${formatDateTime(dayjs.utc(action.manualOverrideSetAt).toDate())}`
              : ""}
          </Text>
        ) : null}
      </div>
      <div className="mt-auto flex items-end justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap gap-2">
          {action.kind === "Manual" ? (
            <Button
              size="sm"
              variant="outline"
              disabled={
                !canOverride ||
                completionPending ||
                phaseIsClosed ||
                action.effectiveStatus === "Closed"
              }
              onClick={() => {
                onToggleCompletion(action.derivedStatus !== "Complete");
              }}
            >
              {action.derivedStatus === "Complete" ? (
                <RotateCcw className="h-3.5 w-3.5" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              {action.derivedStatus === "Complete"
                ? "Mark as incomplete"
                : "Mark as complete"}
            </Button>
          ) : null}
          {canOverride ? (
            action.manualOverride ? (
              <Button size="sm" variant="outline" onClick={onClearStatus}>
                Clear override
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onSetStatus("Blocked");
                  }}
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Block
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onSetStatus("Closed");
                  }}
                >
                  <Lock className="h-3.5 w-3.5" />
                  Close
                </Button>
              </>
            )
          ) : null}
          {taskButtonLabel ? (
            <Button size="sm" variant="outline" onClick={onTaskClick}>
              <ListTodo className="h-3.5 w-3.5" />
              {taskButtonLabel}
            </Button>
          ) : null}
        </div>
        <div className="ml-auto flex shrink-0 flex-col items-end gap-2">
          <Badge
            variant="outline"
            className={cn("gap-1.5 font-medium", style.badge)}
          >
            <StatusIcon className="h-3.5 w-3.5" />
            {partialProgress ? "In progress" : action.effectiveStatus}
          </Badge>
          {action.evaluationHealth !== "Current" ? (
            <Badge
              variant="outline"
              className={cn(
                "gap-1.5 font-normal",
                action.evaluationHealth === "Failed"
                  ? "border-red-200 bg-red-50 text-red-800"
                  : "border-amber-200 bg-amber-50 text-amber-800",
              )}
            >
              {action.evaluationHealth === "Pending" ? (
                <Clock3 className="h-3.5 w-3.5" />
              ) : (
                <ShieldAlert className="h-3.5 w-3.5" />
              )}
              {action.evaluationHealth === "Pending"
                ? "Updating"
                : action.evaluationHealth === "Failed"
                  ? "Update failed"
                  : "Needs configuration"}
            </Badge>
          ) : null}
        </div>
      </div>
    </div>
  );
}
